import { useContext, useEffect } from 'react';
import { MapContext } from 'os-elements';
import { MAP_PAGE } from 'client/utils/routes/routeConstants';

const debounce = (fn) => {
    let handle;
    return (...args) => {
        cancelAnimationFrame(handle);
        handle = requestAnimationFrame(() => {
            fn(...args);
        });
    };
};

const isLocationHash = (str) => {
    const regex = /^#@\d+,\d+(,\d+(\.\d+)?z)?$/;
    return regex.test(str);
};

const extractLocationAndZoom = (locationHash) => {
    const startIndex = locationHash.indexOf('@') + 1;
    let endIndex = locationHash.indexOf('z');
    if (endIndex === -1) {
        endIndex = locationHash.length;
    }
    return locationHash.slice(startIndex, endIndex).split(',').map(parseFloat);
};

const useLocationInUrl = (enabled = true) => {
    const { map } = useContext(MapContext);

    const loadLocationFromHash = () => {
        const hash = window.location.hash;
        if (isLocationHash(hash)) {
            const [x, y, zoom = 15] = extractLocationAndZoom(hash);
            map.getView().setCenter([x, y]);
            map.getView().setZoom(zoom);
        }
    };

    const updateHash = () => {
        const view = map?.getView();
        if (!enabled || !view) { // view is not there when running tests.
            return;
        }
        const center = view.getCenter();
        const easting = parseInt(center[0]);
        const northing = parseInt(center[1]);
        const zoom = view.getZoom().toFixed(1);

        window.history.replaceState(undefined, undefined, `${MAP_PAGE}#@${easting},${northing},${zoom}z`);
    };

    const debouncedUpdate = debounce(updateHash);

    useEffect(() => {
        if (enabled) {
            loadLocationFromHash();
            window.addEventListener('hashchange', loadLocationFromHash);
            updateHash();
            map.on('moveend', debouncedUpdate);
        }
        return () => {
            map.un('moveend', debouncedUpdate);
            window.removeEventListener('hashchange', loadLocationFromHash);
        };
    }, [enabled]);
};

export default useLocationInUrl;

export {
    isLocationHash as _isLocationHash,
    debounce as _debounce,
    extractLocationAndZoom as _extractLocationAndZoom
};
